// src/components/achievements/ProgressTracker.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { UserStats } from '@/types';
import { Target, TrendingUp } from 'lucide-react';

interface ProgressTrackerProps {
  userStats: UserStats;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({ userStats }) => {
  const milestones = [
    { days: 7, label: 'Week Warrior', icon: '🌟' },
    { days: 30, label: 'Month Master', icon: '⚡' },
    { days: 90, label: 'Quarter Champion', icon: '🔥' },
    { days: 180, label: 'Half Year Hero', icon: '💎' },
    { days: 365, label: 'Year Legend', icon: '👑' },
  ];

  const nextMilestone = milestones.find((m) => m.days > userStats.currentStreak) || milestones[milestones.length - 1];
  const daysUntilNext = nextMilestone.days - userStats.currentStreak;
  const progressToNext = (userStats.currentStreak / nextMilestone.days) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5" />
          Next Milestone
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-float">{nextMilestone.icon}</div>
          <h3 className="text-2xl font-bold mb-2">{nextMilestone.label}</h3>
          <p className="text-muted-foreground">
            {daysUntilNext > 0 ? (
              <>
                <span className="text-2xl font-bold text-primary">{daysUntilNext}</span> days to go!
              </>
            ) : (
              <>Achievement Unlocked! 🎉</>
            )}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Progress</span>
            <span>{userStats.currentStreak} / {nextMilestone.days} days</span>
          </div>
          <Progress value={progressToNext} max={100} className="h-4" />
        </div>

        <div className="grid grid-cols-5 gap-2">
          {milestones.map((milestone) => {
            const isUnlocked = userStats.currentStreak >= milestone.days;
            const isCurrent = milestone.days === nextMilestone.days;

            return (
              <motion.div
                key={milestone.days}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`flex flex-col items-center p-2 rounded-lg border-2 ${
                  isUnlocked
                    ? 'border-green-500 bg-green-500/20'
                    : isCurrent
                    ? 'border-primary bg-primary/20 ring-2 ring-primary'
                    : 'border-muted bg-muted/50'
                }`}
              >
                <div className={`text-2xl ${isUnlocked ? '' : 'grayscale opacity-40'}`}>
                  {milestone.icon}
                </div>
                <div className="text-xs font-bold text-center mt-1">{milestone.days}d</div>
              </motion.div>
            );
          })}
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Testing Days</span>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-2xl font-bold text-green-500">{userStats.totalDays}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

